<template>
    <div class="d-flex flex-column h-100 justify-content-between pb-2 pt-4">
        <div v-tooltip="disableTooltip">
            <button class="w-100 w-100 mb-2 bg-lightest darker text-center font-weight-600 py-3 border-light radius-normal pointer" :disabled="rebookDisableValidation" :title="rebookTooltip" @click="rebookReservation">Rebook</button>
            <button class="w-100 w-100 mb-2 bg-lightest color-6 text-center font-weight-600 py-3 border-light radius-normal pointer" :disabled="cancelBookingDisableValidation" @click="deleteReservation">Cancel Booking</button>
            <button class="w-100 w-100 mb-2 bg-lightest darker text-center font-weight-600 py-3 border-light radius-normal pointer" :disabled="sendEmailDisableValidation" @click="sendTicket">{{isSendingTicket? 'Sending Email':'Send Email'}}</button>
        </div>

        <div v-tooltip="disableTooltip">
            <button class="w-100 w-100 mb-2 bg-lightest darker text-center font-weight-600 py-3 border-light radius-normal pointer" :class="{ 'opacity-50 notAllowed': hasUnsavedChanges }" v-if="showInvoiceBtn" :disabled="invoiceDisableValidation" @click="startCreateInvoice">{{ getIsCreatingInvoice? 'Invoicing...':'Invoice' }}</button>
            <button class="w-100 w-100 mb-2 bg-lightest darker text-center font-weight-600 py-3 border-light radius-normal pointer" :disabled="viewTicketDisableValidation" @click="viewTicket">{{isGeneratingViewTicket? 'Generating ticket':'View Ticket'}}</button>
            <button class="w-100 w-100 mb-2 bg-color-1 lightest text-center font-weight-600 py-3 border-light radius-normal pointer" :disabled="saveDisableValidation" @click="saveReservation({printTicket:false, closeModal:false})">Save</button>
        </div>
    </div>
</template>

<script>
    import { mapActions, mapGetters } from 'vuex'
    import { eventHub } from '@/main'
    import { reservationsMixin } from '@/mixins/reservations'

    import PrintJs from 'print-js'

    export default {
        data() {
            return {
                isSendingTicket: false,
                isSendingTicketAndPrint: false,
                isGeneratingViewTicket: false,
                isSavingReservation: false,
                isCancellingReservation: false,
            }
        },
        props: {
            isPhoneValid: { 
                type: Boolean, 
                default: true 
            },
            hasPhoneValue: {
                type: Boolean,
                default: false
            },
            reservation: {
                type: Object
            },
            disabled: {
                type: Boolean,
                default: false
            },
            showAddPayment: {
                type: Boolean
            },
            isToSaveChanges: {
                type: Boolean,
                default: false
            },
            topAreaIsReadyToSubmit: {
                type: Boolean,
                default: false
            },
            titleAreaIsReadyToSubmit: {
                type: Boolean,
                default: false
            }
        },
        created() {
            this.eventHub.$on('cancelReservation',(data)=>{
                this.deleteReservation();
            });
            this.eventHub.$on('refundReservation',(data)=>{
                this.refundReservation();
            });
            this.eventHub.$on('saveReservation',(data)=>{
                this.saveReservation(data);
            });
        },
        beforeDestroy() {
            this.eventHub.$off('cancelReservation');
            this.eventHub.$off('refundReservation');
            this.eventHub.$off('saveReservation');
        },
        watch: {
            async isToSaveChanges(){
                if(this.isToSaveChanges) {
                    this.$emit('savingChanges', true);
                    await this.saveReservation({printTicket:false, closeModal:false}).then(() => {
                        this.$emit('changesSaved');
                        this.$emit('savingChanges', false);
                    })
                }
            }
        },
        computed: {
            ...mapGetters([
                'account',
                'getIsCreatingInvoice',
                'getCompanySettings',
                'getShowInvoiceOption',
                'getReservationBackup',
                'getSelectedReservation'
            ]),
            saveDisableValidation() {
                const hasAnyPhone = this.hasPhoneValue || !!(this.reservation && this.reservation.customer && this.reservation.customer.phone);
                const phoneInvalid = hasAnyPhone ? !this.isPhoneValid : false;

                    return this.disabled || this.isSavingReservation || this.isSendingTicketAndPrint || phoneInvalid || !this.hasUnsavedChanges;
            },
            viewTicketDisableValidation() {
                return  this.disabled ||  this.isReservationReadOnly &&  this.reservation.status != -5;
            },
            invoiceDisableValidation() {
                return this.disabled || this.isReservationReadOnly  && this.reservation.status != -5 || this.isSavingReservation || this.getIsCreatingInvoice || this.hasUnsavedChanges;
            },
            sendEmailDisableValidation() {
                return this.disabled || this.isReservationReadOnly  && this.reservation.status != -5;
            },
            rebookDisableValidation()  {
                return  this.disabled || this.accessPermissionFilter(['restricted']) || this.isReservationReadOnly && this.reservation.status != 5 
                        || this.isCancellingReservation;
            },
            cancelBookingDisableValidation() {
                return  this.disabled || this.accessPermissionFilter(['restricted']) || this.reservation.status == -5 
                        || this.isCancellingReservation || this.reservation.changeStatusTo == -5;
            },
            refundBtnLabel() {
                return  (this.reservation.refunds && this.reservation.refunds.length > 0 
                        && this.reservation.refunds.filter(refund => !refund.id).length > 0)? 'Edit refund' : 'Refund';
            },
            disableRefund() {
                return this.reservation.updateFlags.extras == true || this.reservation.updateFlags.pax == true;
            },
            showInvoiceBtn() {
                return this.getShowInvoiceOption? this.getShowInvoiceOption : false;
            },
            hasUnsavedChanges() {
                const updateFlags = this.reservation.updateFlags;
                const hashasUnsavedChanges = Object.values(updateFlags).some(value => value === true);
                
                return hashasUnsavedChanges;
            },
            disableTooltip(){
                if (this.disabled) {
                    return "Apply edition in pax before";
                }
            },
            rebookTooltip() {
                if (this.reservation && this.reservation.flags && this.reservation.flags.isFullyInvoiced) {
                    return "This booking can't be rebooked because it has already been fully invoiced.";
                }
                return "";
            }
        },
        mixins:[
            reservationsMixin,
        ],
        methods: {
            ...mapActions([
                'updateExistingReservation',
                'closeModal',
                'cancelExistingReservation',
                'setReservation',
                'createInvoice',
                'openModal',
                'updateClonedReservation',
                'setReservationCalculations',
            ]),
            printTicket() {
                PrintJs('static/images/ticket.png');
            },
            async saveReservation(options) {
                if (!options) {
                    options = {
                        closeModal: false
                    }
                }

                // If top area is not ready to submit
                if (!this.topAreaIsReadyToSubmit) {
                    this.$toasted.error('Please review the required fields before proceeding.');
                    
                    return false;
                }

                // If title area is not ready to submit
                if (!this.titleAreaIsReadyToSubmit) {
                    this.$toasted.error('Please review the required fields before proceeding.');
                    
                    return false;
                }

                // If is saving do nothing
                if (this.isSavingReservation || this.isSendingTicketAndPrint) {
                    return false;
                }

                // Validate transport
                if (!this.validateTranposrts()) {
                    this.$toasted.error('Check pickups, information missing');
                    
                    return false;
                }

                options.closeModal = (options.closeModal == undefined) ? true : options.closeModal;

                if (options.printTicket) {
                    this.isSendingTicketAndPrint = true;
                } else {
                    this.isSavingReservation = true;
                }

                // If changing status from not cancelled to cancelled trigger cancellation popup *******
                let proceedToSave = true;
                if (this.reservation.status != this.reservation.originalStatus) {
                    await this.checkStatusChange().then(acceptedStatusChange => {
                        if (acceptedStatusChange == false) {
                            // Abort save reservation
                            proceedToSave = false;
                            
                            return false;
                        } else {
                            // If accepted popup from cancelation
                            if (this.reservation.status == '-5') {
                                options.closeModal = false;
                            }
                        }
                    })
                }

                if (proceedToSave == false) {
                    this.isSavingReservation = false;
                    this.isSendingTicketAndPrint = false;
                    
                    return false;
                }
                // *************************************************************************************


                return this.updateExistingReservation({
                    supplierId: this.account.supplierId,
                    reservation: this.reservation
                }).then(response => {
                    if (response.status && response.status == 200) {
                        this.isSavingReservation = false;
                        this.isSendingTicketAndPrint = false;
                        this.reservation.originalStatus = this.reservation.status;

                        this.$emit('updateThisReservation', this.getSelectedReservation);
                        eventHub.$emit('updateReservationsList', this.reservation);
                        this.$emit('updateReservationsList', response.data);

                        if (options.closeModal) {
                            this.closeModal();
                        }

                        if (options.printTicket) {
                            axios.get('/api/booking/GetTicket', {
                                params: {
                                    bookingId: response.data.id
                                }
                            }).then(response => {
                                this.openByteArrayNewTab(response.data);
                            })
                        }
                    } else {
                        this.isSavingReservation = false;
                        this.isSendingTicketAndPrint = false;
                    }
                    this.$emit('changesSaved');
                    this.$emit('savingChanges', false);
                }).catch(error => {
                    const errData = error && error.response && error.response.data;
                    
                    this.isSavingReservation = false;
                    this.isSendingTicketAndPrint = false;

                    // Special case | extras: prevented by active invoices
                    if (errData && errData.internalCode === "604" && errData.message === "Quantity reduction is not allowed when the booking has active invoices.") {
                        this.resetExtras();

                        return;
                    }

                    if (errData && errData.message && error.response.status === 400) {
                        return;
                    }

                    // Generic error handling
                    this.$toasted.error('An error occurred while updating the reservation.');
                })
            },
            resetExtras() {
                const { bookingComponents } = JSON.parse(JSON.stringify(this.getReservationBackup));

                this.$set(this.reservation, 'bookingComponents', bookingComponents);
                this.$set(this.reservation.updateFlags, 'extras', false);
                this.setReservationCalculations(this.reservation);
            },
            deleteReservation() {
                if (this.isCancellingReservation == true) {
                    return false;
                }

                this.openModal('ReservationCancelReason');
            },
            refundReservation() {
                if (this.disableRefund) {
                    this.$dialog.alert(
                        `<div class="d-flex flex-column justify-content-center">
                            <div class="fs-largest font-weight-600 mb-4">There are unsaved changes!</div>
                            <div class="bold dark">You must save the reservation before refunding.</div>
                        </div>`,
                        { okText: 'Yes', cancelText: 'No' });
                    
                    return false;
                }

                this.openModal('ReservationRefundGeneral');
            },
            rebookReservation() {
                if (this.isReservationReadOnly && this.reservation.status != 5) {
                    this.readOnlyShowMessage();
                    
                    return false;
                }

                if (this.hasUnsavedChanges) {
                    this.$dialog.alert(
                        `<div class="d-flex flex-column justify-content-center">
                            <div class="fs-largest font-weight-600 mb-4">There are unsaved changes!</div>
                            <div class="bold dark">You must save the reservation before rebooking.</div>
                        </div>`,
                        { okText: 'Yes', cancelText: 'No' });
                    
                    return false;
                }

                // Other situations where we block rebook | PreBooked | PreBookCanceled | Pending Cancelation
                if (this.reservation.status == -6 || this.reservation.status == 50 || this.reservation.status == -50) {
                    this.$toasted.info('Unable to rebook this reservation');
                    
                    return false;
                }

                this.setReservation(this.reservation);
                this.updateClonedReservation(this.reservation);
                this.openModal('RebookChooseProduct');
            },
            toggleAddPayment() {
                this.$emit('toggleAddPayment');
            },
            viewTicket() {
                if (this.isReservationReadOnly && this.reservation.status != -5) {
                    this.readOnlyShowMessage();
                    
                    return false;
                }

                if (this.isGeneratingViewTicket) {
                    this.$toasted.info('Generating ticket to view, please wait');
                }

                this.isGeneratingViewTicket = true;
                axios.get('api/booking/GetTicket',{
                    params:{
                        bookingId: this.reservation.id
                    }
                }).then( response => {
                    this.isGeneratingViewTicket = false;
                    this.openByteArrayNewTab(response.data);
                })
            },
            sendTicket() {
                if (this.isReservationReadOnly && this.reservation.status != 5) {
                    this.readOnlyShowMessage();
                    
                    return false;
                }

                if (this.isSendingTicket) {
                    this.$toasted.info('Sending ticket to '+ this.reservation.customer.email+' please wait');
                    
                    return false;
                }

                this.isSendingTicket = true;
                this.saveReservation({
                    printTicket: false,
                    closeModal: false
                }).then(r => {
                    this.$toasted.info('Sending to ' + this.reservation.customer.email);
                    axios.get('api/Booking/SupplierSendTicket',{
                        params:{
                            bookingId: this.reservation.id
                        }
                    }).then(response => {
                        this.isSendingTicket = false;
                        this.$toasted.success('Sent');
                    }).catch(error => {
                        this.isSendingTicket = false;
                        this.$toasted.error('Sent failed');
                    })
                })
            },
            validateTranposrts() {
                let allTransportValid = true;

                for (let transfer of this.reservation.transport.transfers) {
                    var isTransferValid = true;

                    // Validation for non custom
                    if (!transfer.isCustom) {
                        if (!transfer.route || transfer.route == null || transfer.route == undefined || transfer.route.name == "" || !transfer.route.name) {
                            this.$set(transfer, 'errorRoute', true);
                            isTransferValid = false;
                        } else {
                            this.$set(transfer, 'errorRoute', false);
                        }

                        if (!transfer.stop) {
                            this.$set(transfer, 'errorStop', true);
                            isTransferValid = false;
                        } else {
                            this.$set(transfer, 'errorStop', false);
                            if (!transfer.schedule) {
                                this.$set(transfer, 'errorSchedule', true);
                                isTransferValid = false;
                            } else {
                                this.$set(transfer, 'errorSchedule', false);
                            }
                        }
                    } else {
                        // Validate custom transport
                        if (!transfer.stop || transfer.stop == null || transfer.stop == undefined || transfer.stop.name == "" || !transfer.stop.name) {
                            this.$set(transfer, 'errorRoute', true);
                            isTransferValid = false;
                        } else {
                            this.$set(transfer, 'errorRoute', false);
                        }

                        if (!transfer.exitStop || transfer.exitStop == null || transfer.exitStop == undefined || transfer.exitStop.name == "" || !transfer.exitStop.name) {
                            this.$set(transfer, 'errorStop', true);
                            isTransferValid = false;
                        } else {
                            this.$set(transfer, 'errorStop', false);
                        }

                        if (!transfer.schedule || transfer.schedule == null || transfer.schedule == undefined || !transfer.schedule.time) {
                            this.$set(transfer, 'errorTime', true);
                            isTransferValid = false;
                        } else {
                            this.$set(transfer, 'errorTime', false);
                        }

                        if(!transfer.schedule || transfer.schedule == null || transfer.schedule == undefined || !transfer.schedule.date ){
                            this.$set(transfer, 'errorDate', true);
                            isTransferValid = false;
                        } else {
                            this.$set(transfer, 'errorDate', false);
                        }
                    }

                    if (!isTransferValid) {
                        allTransportValid = false;
                    }

                    this.$set(transfer, 'isValid', isTransferValid);
                    this.$forceUpdate();
                }

                return allTransportValid;
            },
            async checkStatusChange() {
                return new Promise(resolve => {
                    resolve();
                });
            },
            startCreateInvoice() {
                if (this.hasUnsavedChanges) {
                    this.$dialog.alert(
                        `<div class="d-flex flex-column justify-content-center">
                            <div class="fs-largest font-weight-600 mb-4">There are unsaved changes!</div>
                            <div class="bold dark">You must save the reservation before invoicing.</div>
                        </div>`,
                        { okText: 'Yes', cancelText: 'No' });

                    return false;
                }

                this.eventHub.$emit('editReservation_toggleCreateInvoice');
            }
        }
    }
</script>

<style lang="scss" scoped></style>



// WEBPACK FOOTER //
// SideButtons.vue?bfefc2be